// Agencia.js

import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Card,
  CardContent,
  Tooltip,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Backdrop,
  CircularProgress,
  useMediaQuery,
  AppBar,
  Toolbar,
  DialogContentText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Tabs,
  Tab,
  Chip,
  InputAdornment,
  Skeleton,
  Rating,
  Menu,
  Fade,
  Stack,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  Assignment as AssignmentIcon,
  Lightbulb as LightbulbIcon,
  Description as DescriptionIcon,
  VideoLibrary as VideoLibraryIcon,
  Straighten as StraightenIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Place as PlaceIcon,
  WorkOutline as WorkIcon,
  MoreVert as MoreVertIcon,
  PersonOutline as PersonOutlineIcon,
  GroupOutlined as GroupOutlinedIcon,
  SentimentDissatisfied as SentimentDissatisfiedIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import api from '../api/axiosConfig';

/* ---------------------------------------------------------------------------
 * Hook utilitaire : debounce
 * --------------------------------------------------------------------------- */
function useDebouncedValue(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

/* ---------------------------------------------------------------------------
 * Composant LoadingBackdrop (actions bloquantes uniquement)
 * --------------------------------------------------------------------------- */
const LoadingBackdrop = ({ open }) => (
  <Backdrop open={open} sx={{ zIndex: (theme) => theme.zIndex.drawer + 9999 }}>
    <CircularProgress color="inherit" />
  </Backdrop>
);

/* ---------------------------------------------------------------------------
 * Fonction utilitaire pour récupérer l'URL de l'avatar d'un joueur
 * --------------------------------------------------------------------------- */
const getPlayerAvatarUrl = (playerId) =>
  playerId
    ? `${process.env.REACT_APP_MEDIA_URL}/photos/${playerId}.png?${new Date().getTime()}`
    : '/path/to/default-player-avatar.png';

/* ---------------------------------------------------------------------------
 * Initiales d'une chaîne (pour l'avatar d'agence/contact)
 * --------------------------------------------------------------------------- */
const getInitials = (str = '') =>
  str
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || '')
    .join('') || '?';

/* ---------------------------------------------------------------------------
 * Couleur déterministe à partir d'une chaîne (avatar d'agence)
 * --------------------------------------------------------------------------- */
const stringToColor = (str = '') => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const palette = ['#1976d2', '#388e3c', '#7b1fa2', '#c2185b', '#0097a7', '#f57c00', '#5d4037', '#455a64'];
  return palette[Math.abs(hash) % palette.length];
};

/* ---------------------------------------------------------------------------
 * Ligne contact (réutilisée desktop/mobile)
 * --------------------------------------------------------------------------- */
function ContactRow({ contact, onEdit, onDelete, t }) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const openMenu = Boolean(menuAnchor);

  const ratingNum = Number(contact.valoracion) || 0;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-of-type': { borderBottom: 'none' },
      }}
    >
      <Avatar
        sx={{
          bgcolor: stringToColor(contact.nom || ''),
          width: 40,
          height: 40,
          fontSize: '0.95rem',
        }}
      >
        {getInitials(contact.nom || 'C')}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25 }}>
          {contact.nom || '—'}
        </Typography>

        <Stack spacing={0.25} sx={{ mb: 0.5 }}>
          {contact.telephone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography
                component="a"
                href={`tel:${contact.telephone}`}
                variant="body2"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {contact.telephone}
              </Typography>
            </Box>
          )}
          {contact.mail && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
              <Typography
                component="a"
                href={`mailto:${contact.mail}`}
                variant="body2"
                sx={{
                  color: 'primary.main',
                  textDecoration: 'none',
                  wordBreak: 'break-all',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {contact.mail}
              </Typography>
            </Box>
          )}
        </Stack>

        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
          {contact.posicion && (
            <Chip
              size="small"
              icon={<WorkIcon sx={{ fontSize: 14 }} />}
              label={contact.posicion}
              variant="outlined"
            />
          )}
          {contact.mercado && (
            <Chip
              size="small"
              icon={<PlaceIcon sx={{ fontSize: 14 }} />}
              label={contact.mercado}
              variant="outlined"
            />
          )}
        </Stack>

        {ratingNum > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
            <Rating value={ratingNum} max={10} size="small" readOnly precision={0.5} />
            <Typography variant="caption" color="text.secondary">
              {ratingNum}/10
            </Typography>
          </Box>
        )}

        {contact.commentaire && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic', mt: 0.5 }}
          >
            « {contact.commentaire} »
          </Typography>
        )}
      </Box>

      <Box>
        <Tooltip title={t('agencia.moreActions')}>
          <IconButton
            size="small"
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            aria-label={t('agencia.moreActions')}
          >
            <MoreVertIcon />
          </IconButton>
        </Tooltip>
        <Menu
          anchorEl={menuAnchor}
          open={openMenu}
          onClose={() => setMenuAnchor(null)}
        >
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onEdit(contact);
            }}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            {t('agencia.contact.editTitle')}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onDelete(contact.id);
            }}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            {t('common.delete')}
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
}

/* ---------------------------------------------------------------------------
 * En-tête d'une carte d'agence
 * --------------------------------------------------------------------------- */
function AgencyHeader({ agency, isOpen, onToggle, onRename, t }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        p: 1,
        mx: -1,
        borderRadius: 2,
        transition: 'background-color .15s',
        '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
      }}
      onClick={onToggle}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Avatar
          sx={{
            bgcolor: stringToColor(agency.agency_name),
            width: 40,
            height: 40,
            fontSize: '0.95rem',
          }}
        >
          {getInitials(agency.agency_name)}
        </Avatar>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {agency.agency_name}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title={t('agencia.renameAgency')}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onRename(agency);
            }}
            size="small"
          >
            <EditIcon fontSize="small" sx={{ color: 'text.secondary' }} />
          </IconButton>
        </Tooltip>
        <IconButton
          size="small"
          aria-label="expand row"
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
        >
          <KeyboardArrowDownIcon
            sx={{
              transition: 'transform .2s',
              transform: isOpen ? 'rotate(180deg)' : 'none',
            }}
          />
        </IconButton>
      </Box>
    </Box>
  );
}

/* ---------------------------------------------------------------------------
 * Chips compteurs (joueurs / agents)
 * --------------------------------------------------------------------------- */
function CountChips({ agency, onPlayers, onAgents, t, size = 'medium' }) {
  return (
    <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
      <Chip
        icon={<PersonOutlineIcon />}
        label={`${agency.countPlayers} ${t('agencia.players')}`}
        onClick={onPlayers}
        clickable
        color="primary"
        variant="filled"
        size={size}
        sx={{ fontWeight: 600 }}
      />
      <Chip
        icon={<GroupOutlinedIcon />}
        label={`${agency.countContacts} ${t('agencia.agents')}`}
        onClick={onAgents}
        clickable
        size={size}
        sx={{
          fontWeight: 600,
          bgcolor: '#e8f5e9',
          color: '#2e7d32',
          '& .MuiChip-icon': { color: '#2e7d32' },
          '&:hover': { bgcolor: '#c8e6c9' },
        }}
      />
    </Stack>
  );
}

/* ---------------------------------------------------------------------------
 * État vide
 * --------------------------------------------------------------------------- */
function EmptyState({ t }) {
  return (
    <Box
      sx={{
        textAlign: 'center',
        py: 8,
        color: 'text.secondary',
      }}
    >
      <SentimentDissatisfiedIcon sx={{ fontSize: 64, mb: 1, opacity: 0.5 }} />
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        {t('agencia.emptyState.title')}
      </Typography>
      <Typography variant="body2">{t('agencia.emptyState.subtitle')}</Typography>
    </Box>
  );
}

/* ---------------------------------------------------------------------------
 * Skeletons pendant le premier chargement
 * --------------------------------------------------------------------------- */
function AgencySkeleton() {
  return (
    <Card sx={{ mb: 2, borderRadius: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Skeleton variant="circular" width={40} height={40} />
          <Skeleton variant="text" width="40%" height={28} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Skeleton variant="rounded" width={120} height={32} />
          <Skeleton variant="rounded" width={120} height={32} />
        </Box>
      </CardContent>
    </Card>
  );
}

/* ---------------------------------------------------------------------------
 * Liste d'agents pour une agence (commune desktop/mobile)
 * --------------------------------------------------------------------------- */
function AgencyContactsPanel({ agency, contacts, onAdd, onEdit, onDelete, t }) {
  const agenciaContacts = contacts
    .filter((c) => c.agency_name === agency.agency_name)
    .sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        backgroundColor: 'grey.50',
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
          {t('agencia.agentsOf')} {agency.agency_name}
        </Typography>
        <Button
          size="small"
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onAdd}
          sx={{ borderRadius: 50, textTransform: 'none', fontWeight: 600 }}
        >
          {t('agencia.addAgent')}
        </Button>
      </Box>

      {agenciaContacts.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {t('agencia.noAgents')}
        </Typography>
      ) : (
        <Box
          sx={{
            backgroundColor: '#fff',
            borderRadius: 2,
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            overflow: 'hidden',
          }}
        >
          {agenciaContacts.map((contact) => (
            <ContactRow
              key={contact.id}
              contact={contact}
              onEdit={onEdit}
              onDelete={onDelete}
              t={t}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

/* ---------------------------------------------------------------------------
 * Version Desktop
 * --------------------------------------------------------------------------- */
function DesktopView(props) {
  const {
    searchTerm,
    setSearchTerm,
    resultsCount,
    agencias,
    contacts,
    openContactsCollapse,
    toggleContactsCollapse,
    handleOpenPlayersModal,
    handleDeleteContact,
    openContactDialog,
    openEditAgency,
    rowRefs,
    loadingInitial,
    t,
  } = props;

  return (
    <Box sx={{ backgroundColor: '#f5f5f7', minHeight: '100vh' }}>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          backgroundColor: '#f5f5f7',
          px: 4,
          pt: 3,
          pb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            {t('agencia.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('agencia.resultsCount', { count: resultsCount })}
          </Typography>
        </Box>

        <TextField
          placeholder={t('agencia.searchPlaceholder')}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchTerm('')}
                  aria-label={t('agencia.clearSearch')}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            backgroundColor: '#fff',
            borderRadius: 2,
            '& .MuiOutlinedInput-root': { borderRadius: 2 },
          }}
        />
      </Box>

      <Box sx={{ px: 4, py: 3 }}>
        {loadingInitial ? (
          <>
            <AgencySkeleton />
            <AgencySkeleton />
            <AgencySkeleton />
          </>
        ) : agencias.length === 0 ? (
          <EmptyState t={t} />
        ) : (
          agencias.map((ag) => (
            <Card
              key={ag.agency_name}
              sx={{
                mb: 2,
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                transition: 'box-shadow .2s',
                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
              }}
              ref={(el) => {
                if (el) rowRefs.current[ag.agency_name] = el;
              }}
            >
              <CardContent>
                <AgencyHeader
                  agency={ag}
                  isOpen={!!openContactsCollapse[ag.agency_name]}
                  onToggle={() => toggleContactsCollapse(ag.agency_name)}
                  onRename={openEditAgency}
                  t={t}
                />

                <CountChips
                  agency={ag}
                  onPlayers={() => handleOpenPlayersModal(ag)}
                  onAgents={() => toggleContactsCollapse(ag.agency_name)}
                  t={t}
                />

                <Collapse
                  in={!!openContactsCollapse[ag.agency_name]}
                  timeout="auto"
                  unmountOnExit
                >
                  <AgencyContactsPanel
                    agency={ag}
                    contacts={contacts}
                    onAdd={() => openContactDialog(null, ag.agency_name)}
                    onEdit={(c) => openContactDialog(c)}
                    onDelete={handleDeleteContact}
                    t={t}
                  />
                </Collapse>
              </CardContent>
            </Card>
          ))
        )}
      </Box>
    </Box>
  );
}

/* ---------------------------------------------------------------------------
 * Version Mobile
 * --------------------------------------------------------------------------- */
function MobileView(props) {
  const {
    searchTerm,
    setSearchTerm,
    resultsCount,
    agencias,
    contacts,
    openContactsCollapse,
    toggleContactsCollapse,
    handleOpenPlayersModal,
    handleDeleteContact,
    openContactDialog,
    openEditAgency,
    rowRefs,
    loadingInitial,
    t,
  } = props;

  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <Box sx={{ pb: 7 }}>
      <AppBar position="fixed" color="inherit" elevation={1}>
        <Toolbar>
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, textAlign: 'center', fontWeight: 700 }}
          >
            {t('agencia.titleMobile')}
          </Typography>
          <IconButton
            onClick={() => setSearchOpen((o) => !o)}
            aria-label={t('agencia.searchAria')}
          >
            {searchOpen ? <CloseIcon /> : <SearchIcon />}
          </IconButton>
        </Toolbar>
        <Collapse in={searchOpen} timeout="auto" unmountOnExit>
          <Box sx={{ px: 2, pb: 1.5 }}>
            <TextField
              placeholder={t('agencia.searchPlaceholderMobile')}
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                      aria-label={t('agencia.clearSearch')}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          </Box>
        </Collapse>
      </AppBar>
      <Toolbar />
      {searchOpen && <Box sx={{ height: 56 }} />}

      <Box sx={{ px: 2, pt: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mb: 1, ml: 0.5 }}
        >
          {t('agencia.resultsCount', { count: resultsCount })}
        </Typography>

        {loadingInitial ? (
          <>
            <AgencySkeleton />
            <AgencySkeleton />
          </>
        ) : agencias.length === 0 ? (
          <EmptyState t={t} />
        ) : (
          agencias.map((ag) => (
            <Box
              key={ag.agency_name}
              sx={{ mb: 2 }}
              ref={(el) => {
                if (el) rowRefs.current[ag.agency_name] = el;
              }}
            >
              <Card variant="outlined" sx={{ borderRadius: 3 }}>
                <CardContent>
                  <AgencyHeader
                    agency={ag}
                    isOpen={!!openContactsCollapse[ag.agency_name]}
                    onToggle={() => toggleContactsCollapse(ag.agency_name)}
                    onRename={openEditAgency}
                    t={t}
                  />

                  <CountChips
                    agency={ag}
                    onPlayers={() => handleOpenPlayersModal(ag)}
                    onAgents={() => toggleContactsCollapse(ag.agency_name)}
                    t={t}
                    size="small"
                  />

                  <Collapse
                    in={!!openContactsCollapse[ag.agency_name]}
                    timeout="auto"
                    unmountOnExit
                  >
                    <AgencyContactsPanel
                      agency={ag}
                      contacts={contacts}
                      onAdd={() => openContactDialog(null, ag.agency_name)}
                      onEdit={(c) => openContactDialog(c)}
                      onDelete={handleDeleteContact}
                      t={t}
                    />
                  </Collapse>
                </CardContent>
              </Card>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}

/* ---------------------------------------------------------------------------
 * Composant Principal : Agencia
 * --------------------------------------------------------------------------- */
export default function Agencia() {
  const { t } = useTranslation();

  const isMobile = useMediaQuery('(max-width:600px)');

  // RÔLE UTILISATEUR
  const [user, setUser] = useState(null);
  useEffect(() => {
    const localStoredUser = JSON.parse(localStorage.getItem('user'));
    if (localStoredUser) setUser(localStoredUser);
  }, []);

  // ÉTATS
  const [agencias, setAgencias] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebouncedValue(searchTerm, 200);

  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const [openContactsCollapse, setOpenContactsCollapse] = useState({});

  // Dialog liste de joueurs
  const [openPlayersDialog, setOpenPlayersDialog] = useState(false);
  const [playersAgencyName, setPlayersAgencyName] = useState('');
  const [playersList, setPlayersList] = useState([]);
  const [positionFilter, setPositionFilter] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  // Dialog Renommer
  const [openEditAgencyDialog, setOpenEditAgencyDialog] = useState(false);
  const [editingAgency, setEditingAgency] = useState(null);
  const [newAgencyName, setNewAgencyName] = useState('');
  const [initialAgencyName, setInitialAgencyName] = useState('');

  // Dialog Contact
  const [openDialogContact, setOpenDialogContact] = useState(false);
  const [editingContactId, setEditingContactId] = useState(null);
  const [contactAgencyName, setContactAgencyName] = useState('');
  const [contactNom, setContactNom] = useState('');
  const [contactTel, setContactTel] = useState('');
  const [contactMail, setContactMail] = useState('');
  const [contactValoracion, setContactValoracion] = useState(0);
  const [contactComentario, setContactComentario] = useState('');
  const [contactPosicion, setContactPosicion] = useState('');
  const [contactMercado, setContactMercado] = useState('');
  const [initialContact, setInitialContact] = useState(null);

  // Dialog suppression (remplace window.confirm)
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  // Paramètre auto-open
  const [autoOpenAgency, setAutoOpenAgency] = useState('');

  const rowRefs = useRef({});

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const paramAgency = searchParams.get('agency') || '';
    setAutoOpenAgency(paramAgency);
  }, []);

  useEffect(() => {
    if (user) loadData();
    // eslint-disable-next-line
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      const roleLower = user?.role?.toLowerCase() || '';

      const res = await api.get('/api/agencia/aggregate', {
        params: { role: roleLower },
      });
      setAgencias(res.data);

      const resC = await api.get('/api/agencia/contacts/all');
      setContacts(resC.data);

      setOpenPlayersDialog(false);
      setPlayersAgencyName('');
      setPlayersList([]);

      if (autoOpenAgency) {
        setOpenContactsCollapse((prev) => ({
          ...prev,
          [autoOpenAgency]: true,
        }));
        setTimeout(() => {
          if (rowRefs.current[autoOpenAgency]) {
            rowRefs.current[autoOpenAgency].scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }, 300);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      alert(t('agencia.loadError'));
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  };

  // Filtrage
  const filteredAgencias = useMemo(() => {
    const term = debouncedSearch.toLowerCase().trim();
    if (!term) return agencias;
    return agencias.filter((ag) => {
      const matchAgencyName = ag.agency_name.toLowerCase().includes(term);
      const agencyContacts = contacts.filter(
        (c) => c.agency_name === ag.agency_name
      );
      const matchContacts = agencyContacts.some((c) =>
        c.nom?.toLowerCase().includes(term)
      );
      return matchAgencyName || matchContacts;
    });
  }, [agencias, contacts, debouncedSearch]);

  // Liste joueurs
  const handleOpenPlayersModal = async (ag) => {
    const agencyName = ag.agency_name;
    setPlayersAgencyName(agencyName);
    setPlayersList([]);
    setOpenPlayersDialog(true);

    try {
      setLoading(true);
      const roleLower = user?.role?.toLowerCase() || '';
      const res = await api.get('/api/agencia/players', {
        params: { agencyName, role: roleLower },
      });
      const sortedPlayers = res.data.sort((a, b) =>
        (a.nom || '').localeCompare(b.nom || '')
      );
      setPlayersList(sortedPlayers);
    } catch (err) {
      console.error('Error al cargar jugadores:', err);
      alert(t('agencia.playerLoadError'));
      setPlayersList([]);
    } finally {
      setLoading(false);
    }
  };

  // Toggle collapse
  const toggleContactsCollapse = (agencyName) => {
    setOpenContactsCollapse((prev) => {
      const newVal = !prev[agencyName];
      const updatedState = { ...prev, [agencyName]: newVal };
      if (newVal) {
        setTimeout(() => {
          if (rowRefs.current[agencyName]) {
            rowRefs.current[agencyName].scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            });
          }
        }, 200);
      }
      return updatedState;
    });
  };

  // Rename
  const openEditAgency = (ag) => {
    setEditingAgency(ag);
    setNewAgencyName(ag.agency_name);
    setInitialAgencyName(ag.agency_name);
    setOpenEditAgencyDialog(true);
  };

  const isAgencyDirty = () =>
    newAgencyName.trim() !== initialAgencyName.trim();

  const closeEditAgencyDialog = (force = false) => {
    if (!force && isAgencyDirty()) {
      if (!window.confirm(t('agencia.unsavedChangesWarning'))) return;
    }
    setOpenEditAgencyDialog(false);
    setEditingAgency(null);
    setInitialAgencyName('');
  };

  const handleSaveEditAgency = async () => {
    if (!editingAgency) return;
    const oldName = editingAgency.agency_name;
    const newName = newAgencyName.trim();
    if (!newName) {
      alert(t('agencia.enterNewName'));
      return;
    }
    try {
      setLoading(true);
      if (newName !== oldName) {
        await api.put('/api/agencia/rename', { oldName, newName });
      }
      alert(t('agencia.agencyRenamed'));
      closeEditAgencyDialog(true);
      loadData();
    } catch (err) {
      console.error('Error al renombrar agencia:', err);
      alert(t('agencia.renameError'));
    } finally {
      setLoading(false);
    }
  };

  // Contact
  const openContactDialog = (contact, agencyName = '') => {
    const snapshot = contact
      ? {
          nom: contact.nom || '',
          telephone: contact.telephone || '',
          mail: contact.mail || '',
          posicion: contact.posicion || '',
          mercado: contact.mercado || '',
          valoracion: contact.valoracion || 0,
          commentaire: contact.commentaire || '',
          agencyName: contact.agency_name || '',
        }
      : {
          nom: '',
          telephone: '',
          mail: '',
          posicion: '',
          mercado: '',
          valoracion: 0,
          commentaire: '',
          agencyName,
        };

    setEditingContactId(contact ? contact.id : null);
    setContactAgencyName(snapshot.agencyName);
    setContactNom(snapshot.nom);
    setContactTel(snapshot.telephone);
    setContactMail(snapshot.mail);
    setContactPosicion(snapshot.posicion);
    setContactMercado(snapshot.mercado);
    setContactValoracion(snapshot.valoracion);
    setContactComentario(snapshot.commentaire);
    setInitialContact(snapshot);
    setOpenDialogContact(true);
  };

  const isContactDirty = useCallback(() => {
    if (!initialContact) return false;
    return (
      contactNom !== initialContact.nom ||
      contactTel !== initialContact.telephone ||
      contactMail !== initialContact.mail ||
      contactPosicion !== initialContact.posicion ||
      contactMercado !== initialContact.mercado ||
      String(contactValoracion) !== String(initialContact.valoracion) ||
      contactComentario !== initialContact.commentaire
    );
  }, [
    initialContact,
    contactNom,
    contactTel,
    contactMail,
    contactPosicion,
    contactMercado,
    contactValoracion,
    contactComentario,
  ]);

  const closeContactDialog = (force = false) => {
    if (!force && isContactDirty()) {
      if (!window.confirm(t('agencia.unsavedChangesWarning'))) return;
    }
    setOpenDialogContact(false);
    setInitialContact(null);
  };

  const handleSaveContact = async () => {
    try {
      setLoading(true);
      if (editingContactId) {
        await api.put(`/api/agencia/contacts/${editingContactId}`, {
          nom: contactNom,
          telephone: contactTel,
          mail: contactMail,
          posicion: contactPosicion,
          mercado: contactMercado,
          valoracion: contactValoracion,
          commentaire: contactComentario,
        });
        alert(t('agencia.contact.updated'));
      } else {
        await api.post('/api/agencia/contacts', {
          agency_name: contactAgencyName,
          nom: contactNom,
          telephone: contactTel,
          mail: contactMail,
          posicion: contactPosicion,
          mercado: contactMercado,
          valoracion: contactValoracion,
          commentaire: contactComentario,
        });
        alert(t('agencia.contact.created'));
      }
      closeContactDialog(true);
      loadData();
    } catch (err) {
      console.error('Error al guardar contacto:', err);
      alert(t('agencia.contact.saveError'));
    } finally {
      setLoading(false);
    }
  };

  // Suppression contact (dialog au lieu de window.confirm)
  const handleDeleteContact = (id) => {
    setDeleteTargetId(id);
  };

  const confirmDeleteContact = async () => {
    if (!deleteTargetId) return;
    try {
      setLoading(true);
      await api.delete(`/api/agencia/contacts/${deleteTargetId}`);
      alert(t('agencia.contact.deleted'));
      setDeleteTargetId(null);
      loadData();
    } catch (err) {
      console.error('Error al eliminar contacto:', err);
      alert(t('agencia.contact.deleteError'));
    } finally {
      setLoading(false);
    }
  };

  const loadingInitial = !hasLoadedOnce && loading;

  const viewProps = {
    searchTerm,
    setSearchTerm,
    resultsCount: filteredAgencias.length,
    agencias: filteredAgencias,
    contacts,
    openContactsCollapse,
    toggleContactsCollapse,
    handleOpenPlayersModal,
    handleDeleteContact,
    openContactDialog,
    openEditAgency,
    rowRefs,
    loadingInitial,
    t,
  };

  return (
    <>
      <LoadingBackdrop open={loading && hasLoadedOnce} />

      {isMobile ? <MobileView {...viewProps} /> : <DesktopView {...viewProps} />}

      {/* Dialog liste joueurs */}
      <Dialog
        open={openPlayersDialog}
        onClose={() => {
          setOpenPlayersDialog(false);
          setPositionFilter([]);
          setTabValue(0);
        }}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {t('agencia.playersOfAgency')}
          </Typography>
          <Chip
            label={playersAgencyName}
            color="primary"
            sx={{ fontWeight: 700 }}
          />
        </DialogTitle>
        <DialogContent dividers>
          {(() => {
            const filteredPlayers = playersList.filter(
              (player) =>
                positionFilter.length === 0 ||
                positionFilter.includes(player.poste)
            );
            const proPlayers = filteredPlayers.filter(
              (p) =>
                p.type === 'Pro' ||
                !p.type ||
                (p.type !== 'Cantera' && p.type !== 'cantera')
            );
            const canteraPlayers = filteredPlayers.filter(
              (p) => p.type === 'Cantera' || p.type === 'cantera'
            );

            const renderPlayerList = (players) => (
              <List dense>
                {players
                  .sort((a, b) => (a.nom || '').localeCompare(b.nom || ''))
                  .map((player) => (
                    <ListItem key={player.id} sx={{ py: 1 }}>
                      <ListItemAvatar>
                        <Avatar src={getPlayerAvatarUrl(player.id)} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <a
                            href={`/players/${player.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            <strong>{player.nom}</strong>
                          </a>
                        }
                        secondary={
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              flexWrap: 'wrap',
                            }}
                          >
                            {(() => {
                              const compDetail = player.competitions_detail || [];
                              const hasComp = compDetail.length > 0;
                              const filterVal = (v) => v && v !== 'null' && v !== 'NULL';
                              const beforeTeam = [
                                player.poste,
                                player.date_naissance
                                  ? new Date(player.date_naissance).getFullYear()
                                  : null,
                                player.taille ? `${player.taille}cm` : null,
                                player.nationalite,
                                player.passport,
                              ]
                                .filter(filterVal)
                                .join(' - ');

                              const equipes = hasComp
                                ? [
                                    ...new Map(
                                      compDetail.map((c) => [c.equipe_id, c.equipe_nom])
                                    ).entries(),
                                  ]
                                : [];
                              const teamDisplay = hasComp
                                ? equipes.map(([, nom]) => nom).join(', ')
                                : player.team;
                              const compNoms = hasComp
                                ? [...new Set(compDetail.map((c) => c.competition_nom))]
                                : [];
                              const leagueDisplay = hasComp
                                ? compNoms.join(', ')
                                : [player.league_1, player.league_2]
                                    .filter(filterVal)
                                    .join(', ');

                              return (
                                <>
                                  {beforeTeam && (
                                    <Typography variant="body2" component="span">
                                      {beforeTeam}
                                    </Typography>
                                  )}
                                  {filterVal(teamDisplay) && (
                                    <>
                                      {beforeTeam && (
                                        <Typography variant="body2" component="span">
                                          {' - '}
                                        </Typography>
                                      )}
                                      {hasComp &&
                                        equipes.map(([eId]) => (
                                          <Box
                                            key={eId}
                                            component="img"
                                            src={`${process.env.REACT_APP_MEDIA_URL}/equipes/${eId}.jpg`}
                                            sx={{
                                              width: 18,
                                              height: 18,
                                              objectFit: 'contain',
                                              borderRadius: '2px',
                                            }}
                                            onError={(e) => {
                                              e.target.style.display = 'none';
                                            }}
                                          />
                                        ))}
                                      <Typography variant="body2" component="span">
                                        {teamDisplay}
                                      </Typography>
                                    </>
                                  )}
                                  {filterVal(leagueDisplay) && (
                                    <Typography variant="body2" component="span">
                                      {' - ' + leagueDisplay}
                                    </Typography>
                                  )}
                                </>
                              );
                            })()}
                          </Box>
                        }
                      />
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 0.5,
                          alignItems: 'center',
                          ml: 1,
                        }}
                      >
                        {player.in_big_board === 1 && (
                          <Tooltip title={t('agencia.icons.ranking')}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: '#1976d2',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: '12px',
                              }}
                            >
                              <StarIcon fontSize="inherit" />
                            </Box>
                          </Tooltip>
                        )}
                        {player.in_follow_list === 1 && (
                          <Tooltip title={t('agencia.icons.tracking')}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: '#9c27b0',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: '12px',
                              }}
                            >
                              <VisibilityIcon fontSize="inherit" />
                            </Box>
                          </Tooltip>
                        )}
                        {player.in_informe === 1 && (
                          <Tooltip title={t('agencia.icons.report')}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: '#4caf50',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: '12px',
                              }}
                            >
                              <AssignmentIcon fontSize="inherit" />
                            </Box>
                          </Tooltip>
                        )}
                        {player.in_intel === 1 && (
                          <Tooltip title={t('agencia.icons.intel')}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: '#ff9800',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: '12px',
                              }}
                            >
                              <LightbulbIcon fontSize="inherit" />
                            </Box>
                          </Tooltip>
                        )}
                        {player.in_fiche_technique === 1 && (
                          <Tooltip title={t('agencia.icons.technicalSheet')}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: '#2196f3',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: '12px',
                              }}
                            >
                              <DescriptionIcon fontSize="inherit" />
                            </Box>
                          </Tooltip>
                        )}
                        {player.in_video === 1 && (
                          <Tooltip title={t('agencia.icons.video')}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: '#9c27b0',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: '12px',
                              }}
                            >
                              <VideoLibraryIcon fontSize="inherit" />
                            </Box>
                          </Tooltip>
                        )}
                        {player.has_medidas === 1 && (
                          <Tooltip title={t('agencia.icons.measurements')}>
                            <Box
                              sx={{
                                width: 20,
                                height: 20,
                                borderRadius: '50%',
                                backgroundColor: '#00bcd4',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontSize: '12px',
                              }}
                            >
                              <StraightenIcon fontSize="inherit" />
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    </ListItem>
                  ))}
              </List>
            );

            const hasPro = proPlayers.length > 0;
            const hasCantera = canteraPlayers.length > 0;

            if (!hasPro && !hasCantera) {
              return <Typography variant="body2">{t('agencia.noPlayers')}</Typography>;
            }

            return (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="position-filter-label">
                    {t('agencia.filterByPosition')}
                  </InputLabel>
                  <Select
                    labelId="position-filter-label"
                    multiple
                    value={positionFilter}
                    onChange={(e) => setPositionFilter(e.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                    label={t('agencia.filterByPosition')}
                  >
                    {(() => {
                      const positionOrder = ['Base', 'Escolta', 'Alero', 'Ala-Pivot', 'Pivot'];
                      const uniquePositions = [
                        ...new Set(playersList.map((p) => p.poste).filter(Boolean)),
                      ];
                      const sortedPositions = uniquePositions.sort((a, b) => {
                        const indexA = positionOrder.indexOf(a);
                        const indexB = positionOrder.indexOf(b);
                        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                        if (indexA !== -1) return -1;
                        if (indexB !== -1) return 1;
                        return a.localeCompare(b);
                      });
                      return sortedPositions.map((position) => (
                        <MenuItem key={position} value={position}>
                          <Checkbox checked={positionFilter.indexOf(position) > -1} />
                          <ListItemText primary={position} />
                        </MenuItem>
                      ));
                    })()}
                  </Select>
                </FormControl>

                <Tabs
                  value={tabValue}
                  onChange={(e, newValue) => setTabValue(newValue)}
                  indicatorColor="primary"
                  textColor="inherit"
                  variant="fullWidth"
                  sx={{
                    mb: 2,
                    '& .MuiTab-root': { fontWeight: 'medium' },
                    '& .Mui-selected': { fontWeight: 'bold' },
                  }}
                >
                  <Tab
                    label={`${t('agencia.proPlayers')} (${proPlayers.length})`}
                    disabled={!hasPro}
                    sx={{
                      color: tabValue === 0 && hasPro ? '#1976d2' : 'text.secondary',
                      '&.Mui-selected': { color: '#1976d2' },
                    }}
                  />
                  <Tab
                    label={`${t('agencia.canteraPlayers')} (${canteraPlayers.length})`}
                    disabled={!hasCantera}
                    sx={{
                      color: tabValue === 1 && hasCantera ? '#ff9800' : 'text.secondary',
                      '&.Mui-selected': { color: '#ff9800' },
                    }}
                  />
                </Tabs>

                {tabValue === 0 && hasPro && <Box>{renderPlayerList(proPlayers)}</Box>}
                {tabValue === 1 && hasCantera && <Box>{renderPlayerList(canteraPlayers)}</Box>}
              </>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPlayersDialog(false)} color="primary">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Renommer Agence */}
      <Dialog
        open={openEditAgencyDialog}
        onClose={() => closeEditAgencyDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t('agencia.renameAgency')}</DialogTitle>
        <DialogContent>
          <TextField
            label={t('agencia.newName')}
            fullWidth
            margin="normal"
            value={newAgencyName}
            onChange={(e) => setNewAgencyName(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeEditAgencyDialog(false)} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSaveEditAgency} variant="contained" color="primary">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog CONTACTO */}
      <Dialog
        open={openDialogContact}
        onClose={() => closeContactDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingContactId
            ? t('agencia.contact.editTitle')
            : t('agencia.contact.addTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {t('agencia.contact.completeFields')}
          </DialogContentText>
          <TextField
            label={t('agencia.contact.associatedAgency')}
            value={contactAgencyName}
            fullWidth
            margin="normal"
            disabled
          />
          <TextField
            label={t('agencia.contact.agentName')}
            value={contactNom}
            onChange={(e) => setContactNom(e.target.value)}
            fullWidth
            margin="normal"
          />
          <TextField
            label={t('agencia.contact.phone')}
            value={contactTel}
            onChange={(e) => setContactTel(e.target.value)}
            fullWidth
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label={t('agencia.contact.email')}
            value={contactMail}
            onChange={(e) => setContactMail(e.target.value)}
            fullWidth
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label={t('agencia.contact.position')}
            value={contactPosicion}
            onChange={(e) => setContactPosicion(e.target.value)}
            fullWidth
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <WorkIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label={t('agencia.contact.market')}
            value={contactMercado}
            onChange={(e) => setContactMercado(e.target.value)}
            fullWidth
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PlaceIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {t('agencia.contact.rating')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Rating
                max={10}
                value={Number(contactValoracion) || 0}
                onChange={(e, val) => setContactValoracion(val || 0)}
                precision={1}
              />
              <Typography variant="caption" color="text.secondary">
                {Number(contactValoracion) || 0}/10
              </Typography>
            </Box>
          </Box>
          <TextField
            label={t('agencia.contact.comment')}
            multiline
            rows={3}
            value={contactComentario}
            onChange={(e) => setContactComentario(e.target.value)}
            fullWidth
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeContactDialog(false)} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSaveContact} variant="contained" color="primary">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog confirmation suppression contact */}
      <Dialog
        open={!!deleteTargetId}
        onClose={() => setDeleteTargetId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t('common.delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('agencia.contact.confirmDelete')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTargetId(null)} color="secondary">
            {t('common.cancel')}
          </Button>
          <Button
            onClick={confirmDeleteContact}
            variant="contained"
            color="error"
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
